import * as actionTypes from './actionTypes';
import { initPriceDefault } from '../../../Util/priceLabel';
import { currentSortBy } from '../../../Util/searchSort';
import { orderByShownPrice } from '../../../Util/shownPriceOrder';

const log = require("loglevel").getLogger("propertyReducer");
log.setLevel("debug");

const safeParseArray = (raw) => {
	if (!raw) return [];
	try {
		const v = JSON.parse(raw);
		return Array.isArray(v) ? v : [];
	} catch {
		return [];
	}
};

const readPersistedArray = (key) =>
	safeParseArray(typeof localStorage !== 'undefined' ? localStorage.getItem(key) : null);

// The price filter is the Budget brackets (Util/priceLabel), and a search opens with none
// ticked: no price filter (13 Oct 2026; it opened on a $2,000 minimum before). initPriceDefault
// returns the brackets this browser has stored, and rewrites a value left by the old slider as
// what it now means, because service.js builds the search request from that key without going
// through Redux.

// Default LOW end of the group budget slider (total for the stay), mirroring the
// main price slider's $1,000. Kept here next to the other group defaults; the
// slider's bounds live in components/GroupAccommodationFilters.
export const GROUP_DEFAULT_MIN_BUDGET = 1000;

// Group-accommodation filters persist across reloads (mirrors how the normal
// search persists its inputs). Written by the setGroupFilters thunk.
const readPersistedGroupFilters = () => {
	try {
		const raw = typeof localStorage !== 'undefined' ? localStorage.getItem('groupFilters') : null;
		const v = raw ? JSON.parse(raw) : null;
		return v && typeof v === 'object' && !Array.isArray(v) ? v : {};
	} catch {
		return {};
	}
};
const persistedGroupFilters = readPersistedGroupFilters();

const initialState = JSON.stringify({
	reservations: [],
	properties: null,
	selectedProperties: [],
	wishListItems: [],
	favorites: null,
	isLoading: false,
	reservationReports: {},
	validsearchdestination: true,
	flagwishlistmodel: false,
	// Search results show the map by default. Session-only (no redux-persist), so this
	// initial value is what every fresh load gets. Group mode still forces it off.
	ShowMapInSearch: true,
	noMenu: false,
	activeCountries: [],
	search: {
		destination: '',
		geo: '',
		dateFrom: '',
		dateTo: '',
		// Flexible search (SearchDatesModal). nights = Nights preset (0 = Any); flexDays = ± window
		// widen in days (0 = exact); flexMode = 'dates' | 'flexible'; flexStay = weekend|week|month;
		// flexMonth = index into the next-12-months grid ('' = none). The modal also mirrors the
		// request-affecting values to localStorage (searchFlexDays / searchStayNights) for service.js.
		nights: parseInt(localStorage.getItem('searchNights')) || 0,
		flexDays: parseInt(localStorage.getItem('searchFlexDays')) || 0,
		flexMode: localStorage.getItem('searchFlexMode') || 'dates',
		flexStay: localStorage.getItem('searchFlexStay') || 'weekend',
		flexMonth: (localStorage.getItem('searchFlexMonth') || '') !== '' ? (parseInt(localStorage.getItem('searchFlexMonth')) || 0) : '',
		adults: 2,
		children: 0,
		bedrooms: 1,
		bathrooms: 1,
		sortBy: '',
		// Admin-only source filter for the merged VTHUB+SHUB search: 'all' | 'vt' | 'sh'.
		sourceFilter: 'all',


		collections: '',
		onDemand: '',
		onPrice: '',
		searchedPriceRange: '',
		searchedTypes: '',
		mustHaves: '',
		amenities: '',
		trigger_search: false,

		selectedPrices: initPriceDefault(),
		selectedTypes: readPersistedArray("selectedTypes"),
		selectedMusthave: readPersistedArray("selectedMusthave"),
		selectedAmenities: readPersistedArray("selectedAmenities"),

		// Group accommodation mode (filters AND the mode itself hydrate from
		// localStorage so a refresh keeps the agent in group mode; results are
		// not persisted — re-running Search hits the BE result cache).
		isGroupMode: persistedGroupFilters.isGroupMode === true,
		totalGuests: persistedGroupFilters.totalGuests ?? 20,
		radiusKm: persistedGroupFilters.radiusKm ?? 5,
		maxProperties: persistedGroupFilters.maxProperties ?? 10,
		// Group budget: same $1,000 starting point as the main price slider, and it
		// filters too (Praveen, 2026-09-24 — "both sliders"). BUDGET_BOUNDS.step is
		// $1,000 so the handle can be put back on $1,000 after being moved. '' is
		// still "no bound", written when the handle sits at the far left.
		minBudget: persistedGroupFilters.minBudget ?? GROUP_DEFAULT_MIN_BUDGET,
		maxBudget: persistedGroupFilters.maxBudget ?? '',
		groupDestinations: Array.isArray(persistedGroupFilters.groupDestinations) ? persistedGroupFilters.groupDestinations : [],
		groupDateFrom: persistedGroupFilters.groupDateFrom ?? '',
		groupDateTo: persistedGroupFilters.groupDateTo ?? '',
		mapToggleBeforeGroup: false,
	},
	clusters: [],
	clustersLoading: false,
	clustersError: null,
	clustersMessage: null,
	clusterTotalResults: 0,
	groupSearchGuests: null,
	visibleClusterCount: 5,
	groupSearchPerformed: false,
	clustersLoadedAt: null,
	groupSearchContext: null,
	searchparam: {
		geo: null,
		adults: null
	},
	interactiveMapLocations: [],
	interactiveMapListings: [],
	interactiveMapPoints: [],
	interactiveMapListingsLoading: false,
	interactiveMapPointsLoading: false,
	interactiveMapSelectedContinent: {},
	interactiveMapSelectedCountry: {},
	interactiveMapSelectedListing: false,
	latestProperties: false,
	interactiveMapContinentCountries: []
});

const propertyReducer = (state = JSON.parse(initialState), action) => {
	switch (action.type) {
		case actionTypes.SET_ALL_ACTIVE_COUNTRIES: {
			return {
				...state,
				activeCountries: action.data
			};
		}
		case 'SET_INTERACTIVE_MAP_CONTINENT_COUNTRIES': {
			return {
				...state,
				interactiveMapContinentCountries: action.payload
			};
		}
		case actionTypes.SET_SELECTED_LISTING: {
			return {
				...state,
				interactiveMapSelectedListing: action.data
			}
		}
		case actionTypes.INTERACTIVE_MAP_LAST_VIEWED_COUNTRY: {
			return {
				...state,
				interactiveMapSelectedCountry: action.data
			}
		}
		case actionTypes.INTERACTIVE_MAP_SELECTED_CONTINENT: {
			return {
				...state,
				interactiveMapSelectedContinent: action.data
			}
		}
		case actionTypes.INTERACTIVE_MAP_LISTINGS: {
			return {
				...state,
				interactiveMapListings: action.data
			}
		}
		case actionTypes.INTERACTIVE_MAP_POINTS: {
			return {
				...state,
				interactiveMapPoints: action.data
			}
		}
		case actionTypes.INTERACTIVE_MAP_LISTINGS_LOADING: {
			return {
				...state,
				interactiveMapListingsLoading: action.data
			}
		}
		case actionTypes.INTERACTIVE_MAP_POINTS_LOADING: {
			return {
				...state,
				interactiveMapPointsLoading: action.data
			}
		}
		case actionTypes.INTERACTIVE_MAP_LOCATION: {
			return {
				...state,
				interactiveMapLocations: action.data
			}
		}
		case actionTypes.UPDATE_PROPERTY_FROM_LINK: {
			return {
				...state,
				noMenu: action.data
			}
		}
		case actionTypes.UPDATE_SHOWINMAPSEARCH: {
			return {
				...state,
				ShowMapInSearch: action.data
			}
		}
		case actionTypes.UPDATE_SEARCH_PARAMS: {
			return {
				...state,
				searchparam: {
					...state.searchparam,
					...action.data
				}
			}
		}
		case actionTypes.UPDATE_SEARCH_VALUES: {
			return {
				...state,
				search: {
					...state.search,
					...action.data
				}
			}
		}
		case actionTypes.LOADING_RESERVATION_REPORTS: {
			return {
				...state,
				isLoading: true
			}
		}
		case actionTypes.RESERVATION_REPORTS_LOADED: {
			return {
				...state,
				reservationReports: action.data,
				isLoading: false
			}
		}
		case actionTypes.LOADING_PROPERTIES: {
			return {
				...state,
				isLoading: true,
				properties: null
			}
		}
		case actionTypes.PROPERTIES_LOADED: {
			return {
				...state,
				properties: action.data,
				isLoading: false
			}
		}
		case actionTypes.LOADING_FAVORITES: {
			return {
				...state,
				isLoading: true
			}
		}
		case actionTypes.FAVORITES_LOADED: {
			return {
				...state,
				favorites: action.data,
				isLoading: false
			}
		}
		case actionTypes.CLEAR_PROPERTIES: {
			return {
				...state,
				properties: null
			}
		}
		case actionTypes.TOGGLE_SELECTED_PROPERTY: {
			const property = action.data;
			let {selectedProperties} = state;

			if(selectedProperties.findIndex(p => p._id === property._id) > -1) {
				selectedProperties = selectedProperties.filter(p => p._id !== property._id);
			}
			else if(selectedProperties.length < 5) {
				selectedProperties = [...selectedProperties, property];

			}

			return {
				...state,
				selectedProperties
			}
		}
		case actionTypes.VALID_SEARCH_DESTINATION_CHECK: {
			return {
				...state,
				validsearchdestination: action.data
			}
		}
		case actionTypes.FLAG_WISHLIST_MODEL: {
			return {
				...state,
				flagwishlistmodel: action.data
			}
		}
		case actionTypes.UPDATE_LATEST_PROPERTIES: {
			return {
				...state,
				latestProperties: action.data
			}
		}
		case actionTypes.RESET_LATEST_PROPERTIES: {
			return {
				...state,
				latestProperties: false
			}
		}
		case actionTypes.SET_GROUP_MODE: {
			return {
				...state,
				search: {
					...state.search,
					isGroupMode: action.data
				}
			}
		}
		case actionTypes.SET_GROUP_FILTERS: {
			return {
				...state,
				search: {
					...state.search,
					...action.data
				}
			}
		}
		case actionTypes.LOADING_CLUSTERS: {
			return {
				...state,
				clustersLoading: true,
				clustersError: null,
				clustersMessage: null
			}
		}
		case actionTypes.CLUSTERS_LOADED: {
			return {
				...state,
				clusters: action.data?.clusters || [],
				clustersMessage: action.data?.message || null,
				// Server-side total (may exceed the fetched page, which is capped
				// at the request limit) — the toolbar shows an honest count.
				clusterTotalResults: action.data?.totalResults ?? (action.data?.clusters?.length || 0),
				// Snapshot of the guest count this search was PRICED for — verify
				// must not recompute per-guest against a later-edited form value.
				groupSearchGuests: action.data?.searchParams?.totalGuests ?? state.search?.totalGuests ?? null,
				clustersLoading: false,
				clustersError: null,
				visibleClusterCount: 5,
				groupSearchPerformed: true,
				// Results are intentionally kept across group-mode exits; this
				// timestamp lets the UI say how old they are ("Last searched …").
				clustersLoadedAt: Date.now(),
				// Criteria snapshot the search RAN with (like groupSearchGuests) —
				// the header meta reads this, never the editable live form.
				groupSearchContext: action.data?.requestContext || null
			}
		}
		case actionTypes.CLUSTERS_FAILED: {
			return {
				...state,
				clusters: [],
				clustersError: action.data || 'Search failed',
				clustersMessage: null,
				clustersLoading: false,
				groupSearchPerformed: true,
				clustersLoadedAt: null,
				groupSearchContext: null
			}
		}
		case actionTypes.CLUSTERS_CANCELLED: {
			// User cancelled a running search: stop the spinner, keep whatever
			// results were on screen before.
			return {
				...state,
				clustersLoading: false
			}
		}
		case actionTypes.CLUSTER_QUOTES_VERIFIED: {
			// Live-verified quotes for one cluster replace its estimates; the
			// cluster totals are recomputed so the header always equals the sum
			// of the visible member prices.
			const { clusterIndex, quotes } = action.data || {};
			if (clusterIndex == null || !quotes) return state;
			const clusters = state.clusters.map((cluster, i) => {
				if (i !== clusterIndex) return cluster;
				let total = 0;
				const properties = (cluster.properties || []).map((p) => {
					const verifiedQuote = p.id != null ? quotes[p.id] : undefined;
					const useVerified = verifiedQuote && verifiedQuote.totalAmount > 0;
					const quote = useVerified ? { ...verifiedQuote, verified: true } : p.quote;
					total += quote?.totalAmount || 0;
					return { ...p, quote, verified: !!useVerified };
				});
				return {
					...cluster,
					properties,
					totalPrice: total,
					verified: true,
					avgPricePerNight: cluster.nights ? Math.round(total / cluster.nights) : cluster.avgPricePerNight,
					// Use the guest count the search was priced for (snapshotted at
					// CLUSTERS_LOADED), not the live form value the agent may have
					// edited since.
					pricePerGuest: state.groupSearchGuests
						? Math.round(total / state.groupSearchGuests)
						: cluster.pricePerGuest
				};
			});
			return { ...state, clusters };
		}
		case actionTypes.PATCH_LIVE_SEARCH_PRICES: {
			// Overlay the live-quote price onto the already-rendered search cards. The
			// map is keyed by listing._id and ONLY contains strictly-positive prices
			// (the thunk drops anything non-positive), so an unmatched or dropped listing
			// keeps its stored (>0-guarded) calculatedPrice — a €0 can never be written here.
			const patch = action.data || {};
			if (!patch || !Object.keys(patch).length) return state;
			const listings = state.properties?.listings;
			if (!Array.isArray(listings) || !listings.length) return state;
			let touched = false;
			const nextListings = listings.map((property) => {
				const id = property?.listing?._id;
				const p = id != null ? patch[id] : undefined;
				if (!p || !(Number(p.totalAmount) > 0)) return property; // no positive live price → leave stored
				touched = true;
				return {
					...property,
					calculatedPrice: {
						...(property.calculatedPrice || {}),
						totalAmount: p.totalAmount,
						totalTaxes: p.totalTaxes,
						agencyCommission: p.agencyCommission,
						liveVerified: true,
					},
					totalprice: p.totalprice,
				};
			});
			if (!touched) return state;
			// The page was sorted on the prices the cards started with; a price sort follows
			// the prices they show now (Util/shownPriceOrder.js).
			const ordered = orderByShownPrice(nextListings, state.search?.sortBy || currentSortBy());
			return { ...state, properties: { ...(state.properties || {}), listings: ordered } };
		}
		case actionTypes.LOAD_MORE_CLUSTERS: {
			return {
				...state,
				visibleClusterCount: state.visibleClusterCount + 5
			}
		}
		case actionTypes.RESET_CLUSTERS: {
			return {
				...state,
				clusters: [],
				clustersError: null,
				clustersMessage: null,
				clustersLoading: false,
				visibleClusterCount: 5,
				groupSearchPerformed: false,
				clustersLoadedAt: null,
				groupSearchContext: null
			}
		}
		default:
			return state;
	}
};

export default propertyReducer;
